import React, { useId } from 'react';
import styled from 'styled-components';
import { devices } from '../../data/constants';
import { FieldTooltip } from '../InfoBubble';

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: ${({ $width }) => $width || '140px'};
  max-width: ${({ $maxWidth }) => $maxWidth || '200px'};
  flex: ${({ $flex }) => $flex || '0 0 auto'};

  @media only screen and ${devices.md} {
    width: 100%;
    max-width: 100%;
    min-width: 100%;
  }
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.cardText};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  padding-right: ${({ $suffix }) => $suffix ? '36px' : '12px'};
  padding-left: ${({ $prefix }) => $prefix ? '28px' : '12px'};
  border: 1px solid ${({ theme, $hasError }) => $hasError ? theme.colors.errorColor : theme.colors.borderColor};
  border-radius: 8px;
  font-size: 14px;
  font-family: ${({ theme }) => theme.colors.ff};
  background: ${({ theme }) => theme.colors.cardBG};
  color: ${({ theme }) => theme.colors.cardText};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.activeColor};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.activeColor}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.subTextColor};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.opacityColor};
    cursor: not-allowed;
  }
`;

const Prefix = styled.span`
  position: absolute;
  left: 12px;
  color: ${({ theme }) => theme.colors.subTextColor};
  font-size: 14px;
  pointer-events: none;
`;

const Suffix = styled.span`
  position: absolute;
  right: 12px;
  color: ${({ theme }) => theme.colors.subTextColor};
  font-size: 14px;
  pointer-events: none;
`;

const ErrorText = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.errorColor};
  margin-top: 4px;
`;

const FormField = ({
  label,
  name,
  type = 'number',
  value,
  onChange,
  onFocus,
  placeholder,
  prefix,
  suffix,
  error,
  tooltip,
  width,
  maxWidth,
  flex,
  min,
  max,
  step,
  disabled = false,
  autoComplete = 'off',
}) => {
  const generatedId = useId();
  const fieldId = name || generatedId;
  const fieldName = name || label?.toLowerCase().replace(/\s+/g, '-') || generatedId;

  return (
    <FieldContainer $width={width} $maxWidth={maxWidth} $flex={flex}>
      <Label htmlFor={fieldId}>
        {label}
        {tooltip && <FieldTooltip text={tooltip} />}
      </Label>
      <InputWrapper>
        {prefix && <Prefix>{prefix}</Prefix>}
        <StyledInput
          id={fieldId}
          type={type}
          name={fieldName}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          placeholder={placeholder}
          autoComplete={autoComplete}
          $prefix={prefix}
          $suffix={suffix}
          $hasError={!!error}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
        />
        {suffix && <Suffix>{suffix}</Suffix>}
      </InputWrapper>
      {error && <ErrorText>{error}</ErrorText>}
    </FieldContainer>
  );
};

export default FormField;
